import { setSnakbar } from 'actions/app_action';
import { AnyObject } from 'chart.js/dist/types/basic';
import { FormFieldTypes } from 'common/enums';
import { CrmName } from 'components/aufgaben-component/task.prop';
import { editTask } from 'components/aufgaben-component/utils';
import { useOverlay } from 'hooks';
import useAlert from 'hooks/use-alert';
import moment from 'moment';
import { FileProps } from 'pages/vehicle-return-page/vehicle-return-page.props';
import { useEffect, useMemo } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { errorMessages } from 'utils/error-messages';

export const useUploadDocumentModal = (
  reactHookFormData: UseFormReturn<FieldValues, any>,
  taskId: string,
  files: FileProps[],
  isManagment: boolean,
  refetch: () => void
) => {
  const { handleSubmit, setValue, reset } = reactHookFormData;
  const [showOverlay, hideOverlay] = useOverlay();
  const dispatch = useDispatch();
  const { closeAlert } = useAlert();

  const formFields = useMemo(() => {
    return [
      {
        name: CrmName.Files,
        type: FormFieldTypes.File,
        defaultValue: [],
        dropzoneOptions: {
          maxFiles: 5,
          maxSize: 1024 * 1024 * 5,
          multiple: true,
          accept: ['.pdf', '.jpeg', '.jpg', '.png']
        },

        validation: {
          required: errorMessages.required
        }
      },
      {
        name: CrmName.Description,
        type: FormFieldTypes.TextArea,
        defaultValue: '',
        label: 'Hinweis',
        textAreaProps: {
          maxHeight: 400,
          minRows: 4
        },
        validation: {
          maxLength: { value: 254, message: errorMessages.getMaxTextLength(254) }
        }
      }
    ];
  }, []);

  const convertFilesForField = (files: FileProps[]) => {
    return files.map(file => {
      return {
        path: file.path,
        lastModified: moment(file.modifiedOn).valueOf(),
        lastModifiedDate: moment(file.modifiedOn).toISOString(),
        name: file.fileName,
        size: Number(file.size),
        id: file.fileId
      };
    });
  };

  const onSubmit = () => {
    handleSubmit(async data => {
      closeAlert();
      showOverlay();
      const existingFilesIds = data[CrmName.Files]
        .filter((file: AnyObject) => file.id)
        .map((file: AnyObject) => file.id);
      const formData = new FormData();
      formData.append('id', taskId);
      formData.append('FilesComment', data[CrmName.Description]);
      formData.append('existingFiles', JSON.stringify(existingFilesIds));
      formData.append('isManagment', JSON.stringify(isManagment));

      if (data[CrmName.Files]) {
        data[CrmName.Files]
          .filter((file: AnyObject) => !file.id)
          .forEach((file: File) => {
            formData.append('newfiles', file);
          });
      }

      editTask(formData)
        .then(response => {
          refetch();
          reset();
          dispatch(
            setSnakbar({
              isOpen: true,
              message: response.data.isSuccess
                ? 'Die Aufgabe wurde erfolgreich aktualisiert.'
                : 'Fehler, die Aufgabe wurde nicht geändert.',
              type: response.data.level.toLowerCase()
            })
          );
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          hideOverlay();
        });
    })();
  };

  useEffect(() => {
    setValue(CrmName.Files, convertFilesForField(files), { shouldDirty: false });
  }, []);

  return { formFields, onSubmit };
};
